import { useMemo, useState } from 'react';

import { OrgTreeChart } from '@/components/OrgTreeChart';
import { mutedText } from '@/components/personStyles';
import { departmentNodes } from '@/lib/directory/orgTreeNodes';
import { siteUnitBlocks } from '@/lib/directory/siteUnitBlocks';
import { type DirectoryUnit } from '@/lib/directory/useDirectory';
import { useDepartments, useSites } from '@/lib/queries';
import type { Site } from '@/lib/types';
import { SiteOrgChart } from '@/pages/hr/OrgChartView';

/**
 * 조직도 <b>도식</b> — 직원찾기의 [조직도] 를 켜면 목록 자리에 서는 화면 (사용자 지시 2026-09-28).
 *
 * <h2>본사와 현장을 알약으로 가른다 (사용자 지시 2026-10-01)</h2>
 * 둘을 한 화면에 위아래로 쌓았더니 「지저분하다」 — 본사 트리 아래 현장 스무 칸이 또 서서 어디를
 * 보라는 것인지가 없었다. 본사는 회사 조직 트리({@link OrgTreeChart}), 현장은 <b>먼저 목록</b>을 보이고
 * 하나를 누르면 그 현장의 조직도(현장 양식 판 · {@link SiteOrgChart} 읽기 전용)가 선다.
 *
 * <h2>직급 순서는 서버가 정한 그대로다</h2>
 * 받은 순서를 <b>다시 세우지 않는다</b>. 서버가 직급 사다리(회장 → 사원)로 세워 보내므로
 * ({@code DirectoryService.MEMBER_ORDER}), 여기서 또 세우면 규칙이 두 곳에 생기고 언젠가 갈린다.
 */
export function DirectoryOrgChart({
  axes,
  selectedId,
  onPick,
  searching = false,
}: {
  /** 본사·현장 두 갈래. 비어 있는 갈래는 부르는 쪽이 이미 빼고 준다. */
  axes: Array<{ key: string; label: string; units: DirectoryUnit[] }>;
  selectedId: string | null;
  /** 사람을 눌렀을 때. 부르는 쪽이 명함을 띄운다. */
  onPick: (personnelId: string) => void;
  /** 검색 중이면 걸린 사람이 든 가지만 남긴다 — 빈 부서 스무 칸 사이에서 한 사람을 찾게 하지 않는다. */
  searching?: boolean;
}) {
  const { data: departments = [] } = useDepartments();
  const { data: sites = [] } = useSites();
  const [axis, setAxis] = useState<'HQ' | 'SITE'>('HQ');
  /** 현장 갈래에서 누른 현장. null 이면 목록이다. */
  const [openSite, setOpenSite] = useState<string | null>(null);

  const hqUnits = useMemo(() => axes.find((item) => item.key === 'HQ')?.units ?? [], [axes]);
  const siteUnits = useMemo(() => axes.find((item) => item.key === 'SITE')?.units ?? [], [axes]);
  const hqNodes = useMemo(() => departmentNodes(departments, hqUnits), [departments, hqUnits]);

  if (axes.length === 0) {
    return <p style={{ ...mutedText, padding: '120px 0', textAlign: 'center' }}>조직도가 비어 있습니다.</p>;
  }

  const openSiteId = openSite ? sites.find((site) => site.name === openSite)?.id ?? null : null;

  return (
    <div>
      <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: '8px' }}>
        {([['HQ', '본사'], ['SITE', '현장']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={axis === key}
            className={`selector-chip${axis === key ? ' active' : ''}`}
            onClick={() => {
              setAxis(key);
              setOpenSite(null);
            }}
          >
            {label}
          </button>
        ))}

        {axis === 'SITE' && openSite && (
          <>
            <button type="button" className="btn-ghost" style={{ fontSize: '12px' }} onClick={() => setOpenSite(null)}>
              ← 현장 목록
            </button>
            <span style={{ fontSize: '13px', fontWeight: 700 }}>{openSite}</span>
          </>
        )}
      </div>

      {/* 본사 트리는 스크롤 칸을 스스로 가진다 — 확대·끌기가 그 칸의 스크롤을 민다. 바깥에 또 감싸면 두 겹이 된다. */}
      {axis === 'HQ' ? (
        hqNodes.length === 0 ? (
          <p style={{ ...mutedText, padding: '80px 0', textAlign: 'center' }}>본사 조직이 비어 있습니다.</p>
        ) : (
          <OrgTreeChart nodes={hqNodes} selectedId={selectedId} onPick={onPick} prune={searching} maxHeight="560px" />
        )
      ) : (
      <div style={{ maxHeight: '560px', overflowY: 'auto' }}>
        {openSite === null ? (
          <SiteList units={siteUnits} sites={sites} onOpen={setOpenSite} />
        ) : openSiteId ? (
          <SiteOrgChart siteId={openSiteId} readOnly />
        ) : (
          /* 현장 목록에 없는 이름(배치만 남은 옛 현장) — 양식 판은 없고 사람만 있다. */
          <OrgTreeChart
            nodes={siteUnits
              .filter((unit) => unit.unitName === openSite)
              .map((unit) => ({ id: `site:${unit.unitName}`, name: unit.unitName, parentId: null, members: unit.members, editable: false }))}
            selectedId={selectedId}
            onPick={onPick}
          />
        )}
      </div>
      )}
    </div>
  );
}

/**
 * 현장 목록 — 이름과 인원. 누르면 그 현장의 조직도가 선다. 순서는 주소록이 준 그대로(현장 관리의 차례).
 *
 * <p>국내 진행 · 해외 진행 · 예정 · 평가 · 준공으로 묶는다({@link siteUnitBlocks} · 사용자 지시 2026-10-16).
 */
function SiteList({ units, sites, onOpen }: { units: DirectoryUnit[]; sites: Site[]; onOpen: (name: string) => void }) {
  if (units.length === 0) {
    return <p style={{ ...mutedText, padding: '80px 0', textAlign: 'center' }}>현장이 비어 있습니다.</p>;
  }

  const blocks = siteUnitBlocks(units, sites);

  return (
    <div className="space-y-3">
      {blocks.map((block) => (
        <div key={block.key}>
          {block.label && (
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', margin: '0 2px 6px' }}>
              {block.label}
            </div>
          )}
          <SiteCards units={block.units} onOpen={onOpen} />
        </div>
      ))}
    </div>
  );
}

function SiteCards({ units, onOpen }: { units: DirectoryUnit[]; onOpen: (name: string) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', padding: '2px' }}>
      {units.map((unit) => (
        <button
          key={unit.unitName}
          type="button"
          onClick={() => onOpen(unit.unitName)}
          className="org-tree-member"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            textAlign: 'left',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {unit.unitName}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', flexShrink: 0 }}>{unit.members.length}명</span>
        </button>
      ))}
    </div>
  );
}
